import { useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { copyRawJson, prettyRawJson } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";
import { shortDate } from "../../lib/format";

interface AssetLedgerRow {
  id: number;
  asset_type: string;
  amount: number;
  balance_after?: number | null;
  activity_code?: string | null;
  reason_code: string;
  unique_key?: string | null;
  source_record_id?: string | null;
  note?: string | null;
  created_at: string;
}

interface VoucherSummaryRow {
  voucher_type: string;
  status: string;
  count: number;
}

interface AssetMemberDetail {
  member: { member_account: string; status_label: string };
  assets?: { gems: number; vouchers?: VoucherSummaryRow[]; glory_value?: number };
  asset_ledgers?: AssetLedgerRow[];
  invite_status?: { is_unlocked: boolean };
}

export function AdminAssetsPage() {
  const isLocalDev = isLocalDevFrontend();
  const [account, setAccount] = useState(isLocalDev ? "test006" : "");
  const [detail, setDetail] = useState<AssetMemberDetail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function search() {
    const trimmed = account.trim();
    if (!trimmed) {
      setError("請輸入會員帳號");
      return;
    }
    setLoading(true);
    setError("");
    try {
      setDetail(await api<AssetMemberDetail>(`/api/admin/members/${encodeURIComponent(trimmed)}`, { headers: adminHeaders() }));
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : "查詢失敗");
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setAccount("");
    setDetail(null);
    setError("");
  }

  return (
    <AdminLayout title="資產查詢" actions={<button onClick={search} disabled={loading}>{loading ? "查詢中..." : "查詢"}</button>}>
      <AdminFilterBar>
        <label>
          會員帳號
          <input value={account} onChange={(event) => setAccount(event.target.value)} placeholder={isLocalDev ? "test006" : "請輸入會員帳號"} />
        </label>
        <button type="button" onClick={search} disabled={loading}>{loading ? "查詢中..." : "查詢"}</button>
        <button className="secondary" type="button" onClick={reset}>重設</button>
      </AdminFilterBar>

      {error ? <section className="admin-card"><p className="error-box">{error}</p></section> : null}

      {detail ? (
        <>
          <section className="admin-card">
            <div className="admin-card-head"><span>ASSET SUMMARY</span><h2>{detail.member.member_account} 資產總覽</h2></div>
            <div className="summary-list">
              <p>會員狀態：{detail.member.status_label}</p>
              <p>寶石碎片：{detail.assets?.gems ?? 0}</p>
              <p>紅包券：{voucherCount(detail.assets?.vouchers)}</p>
              <p>閃耀值：{detail.assets?.glory_value ?? 0}</p>
              <p>邀友支線：{detail.invite_status?.is_unlocked ? "已開啟" : "尚未開啟"}</p>
            </div>
          </section>

          <section className="admin-card">
            <div className="admin-card-head"><span>LEDGERS</span><h2>最近資產流水</h2></div>
            <AssetLedgerTable rows={detail.asset_ledgers ?? []} />
          </section>
        </>
      ) : (
        <section className="admin-card">
          <p>請輸入會員帳號查詢。資產查詢不會撈全站資料。</p>
        </section>
      )}
    </AdminLayout>
  );
}

function AssetLedgerTable({ rows }: { rows: AssetLedgerRow[] }) {
  const [copyMessage, setCopyMessage] = useState("");
  async function copy(row: AssetLedgerRow) {
    await copyRawJson(row);
    setCopyMessage(`已複製資產流水 #${row.id}`);
  }
  if (!rows.length) return <p>目前沒有資產流水。</p>;
  return (
    <div className="table-wrap">
      {copyMessage ? <p className="result">{copyMessage}</p> : null}
      <table>
        <thead>
          <tr>
            <th>時間</th>
            <th>類型</th>
            <th>變動</th>
            <th>餘額</th>
            <th>原因</th>
            <th>來源</th>
            <th>原始資料</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{shortDate(row.created_at)}</td>
              <td>{assetTypeLabel(row.asset_type)}</td>
              <td>{row.amount}</td>
              <td>{row.balance_after ?? "-"}</td>
              <td>{reasonLabel(row.reason_code)}</td>
              <td>{row.note ?? sourceLabel(row.source_record_id ?? row.activity_code)}</td>
              <td>
                <details className="raw-details">
                  <summary>查看原始資料</summary>
                  <pre>{prettyRawJson(row)}</pre>
                </details>
                <button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function voucherCount(rows?: VoucherSummaryRow[]) {
  if (!rows?.length) return "0";
  const total = rows.reduce((sum, row) => sum + Number(row.count ?? 0), 0);
  return `${total}（${rows.map((row) => `${voucherTypeLabel(row.voucher_type)} ${statusLabel(row.status)} ${row.count}`).join("，")}）`;
}

function assetTypeLabel(type: string) {
  if (type === "gem") return "寶石碎片";
  if (type === "glory" || type === "glory_value") return "閃耀值";
  return type;
}

function voucherTypeLabel(type: string) {
  if (type === "graduate_launch_red_packet") return "畢業啟航紅包券";
  if (type === "advanced_launch_red_packet") return "高級啟航紅包券";
  if (type === "graduate_start") return "畢業啟航紅包券（legacy）";
  if (type === "advanced_start") return "高級啟航紅包券（legacy）";
  if (type === "newbie_bonus_red_packet") return "新手加碼紅包券";
  if (type === "starter_bonus") return "新手加碼紅包券（legacy）";
  if (type === "newbie_launch_red_packet") return "新人啟航紅包券";
  if (type === "weekly_bright_red_packet") return "週耀紅包券";
  if (type === "weekly_glory") return "週耀紅包券（legacy）";
  return type;
}

function reasonLabel(reason: string) {
  if (reason === "step3_second_deposit") return "新人第 3 關二存加碼";
  if (reason === "step5_third_deposit") return "新人第 5 關三存加碼";
  if (reason === "step6_red_packet_completed") return "新人第 6 關紅包完成";
  if (reason === "step6_red_packet_draw") return "新人第 6 關紅包抽獎";
  if (reason === "gem_exchange_newbie_bonus") return "寶石兌換新手加碼紅包券";
  if (reason.startsWith("redeem_")) return "寶石兌換";
  return reason;
}

function statusLabel(status: string) {
  if (status === "available") return "可用";
  if (status === "used") return "已使用";
  if (status === "expired") return "已過期";
  return status;
}

function sourceLabel(source?: string | null) {
  if (!source) return "-";
  if (source === "gems") return "寶石兌換";
  if (source === "newbie") return "新人七關";
  if (source === "newbie_step5_completed") return "新人第 5 關完成";
  if (source === "newbie_step6_red_packet") return "新人第 6 關紅包";
  return source;
}
